import { useEffect, useState } from "react";
import { ShieldCheck, AlertTriangle, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const DISPOSITION_STYLE = {
  delete: "bg-red-50 text-red-700",
  anonymise: "bg-amber-50 text-amber-700",
  retain: "bg-[#1A1B1A]/[0.05] text-[#4A4B4A]",
};
const DISPOSITION_LABEL = { delete: "Delete", anonymise: "Anonymise", retain: "Retain" };
const SUGGESTED_PREFIX = "Suggested: ";

const STATUS_LABEL = {
  received: "Received",
  identity_verified: "Identity verified",
  under_review: "Under review",
  approved: "Approved",
  partially_approved: "Partially approved",
  refused: "Refused",
  completed: "Completed",
};

export const PrivacyDataRights = ({ patientId, patientName, canManage }) => {
  const [requests, setRequests] = useState([]);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [requesterName, setRequesterName] = useState(patientName || "");
  const [scope, setScope] = useState("");

  const load = async () => {
    try {
      const [{ data: list }, { data: impact }] = await Promise.all([
        api.get(`/privacy/requests?patient_id=${patientId}`),
        api.get(`/privacy/patients/${patientId}/preview`),
      ]);
      setRequests(list.requests || []);
      setPreview(impact);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load privacy requests");
    }
  };

  useEffect(() => { if (canManage) load(); }, [patientId, canManage]);

  if (!canManage) {
    return (
      <p className="text-xs text-[#757675]" data-testid="privacy-no-access">
        Only the clinic owner or an admin can handle privacy and data rights requests.
      </p>
    );
  }

  const createRequest = async () => {
    setBusy(true);
    try {
      await api.post("/privacy/requests", {
        patient_id: patientId, request_type: "erasure",
        requester_name: requesterName || patientName || "Patient", scope,
      });
      setScope("");
      toast.success("Erasure request recorded");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create the request");
    }
    setBusy(false);
  };

  const act = async (requestId, path, body, successMessage) => {
    setBusy(true);
    try {
      await api.post(`/privacy/requests/${requestId}/${path}`, body);
      toast.success(successMessage);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action failed");
    }
    setBusy(false);
  };

  return (
    <div className="space-y-6" data-testid="privacy-data-rights">
      <div className="bg-white rounded-2xl border border-[#1A1B1A]/[0.06] p-6">
        <h3 className="text-sm font-semibold text-[#1A1B1A] flex items-center gap-2">
          <ShieldCheck className="w-4 h-4 text-[#205B5A]" /> Privacy &amp; data rights
        </h3>
        <p className="text-xs text-[#757675] mt-1">
          Archiving keeps the whole record and is the normal way to close a patient file.
          An erasure request is reviewed and decided here before anything is removed.
        </p>

        <div className="mt-4 grid sm:grid-cols-2 gap-3">
          <label className="text-xs text-[#757675]">
            Requested by
            <input value={requesterName} onChange={(e) => setRequesterName(e.target.value)}
              data-testid="privacy-requester-input"
              className="mt-1 w-full border border-[#1A1B1A]/10 rounded-xl px-3 py-2 text-xs text-[#1A1B1A]" />
          </label>
          <label className="text-xs text-[#757675]">
            What has been asked for
            <input value={scope} onChange={(e) => setScope(e.target.value)}
              placeholder="e.g. delete my marketing data"
              data-testid="privacy-scope-input"
              className="mt-1 w-full border border-[#1A1B1A]/10 rounded-xl px-3 py-2 text-xs text-[#1A1B1A]" />
          </label>
        </div>
        <button onClick={createRequest} disabled={busy} data-testid="privacy-create-request-btn"
          className="mt-3 bg-[#205B5A] text-white px-3.5 py-1.5 rounded-full text-[11px] font-medium hover:bg-[#174544] disabled:opacity-50 flex items-center gap-1.5">
          {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Record erasure request
        </button>
      </div>

      {preview && (
        <div className="bg-white rounded-2xl border border-[#1A1B1A]/[0.06] p-6" data-testid="privacy-preview">
          <h4 className="text-sm font-semibold text-[#1A1B1A]">
            {preview.heading || "Suggested handling"} if an erasure is approved
          </h4>
          <p className="text-xs text-[#757675] mt-1">{preview.note}</p>
          <p className="text-[11px] text-[#757675] mt-1.5 italic" data-testid="privacy-disclaimer">
            {preview.disclaimer}
          </p>
          <div className="flex gap-2 mt-3 text-[11px]">
            {["delete", "anonymise", "retain"].map((d) => (
              <span key={d} className={`rounded-full px-2 py-0.5 ${DISPOSITION_STYLE[d]}`}
                data-testid={`privacy-total-${d}`}>
                {SUGGESTED_PREFIX}{DISPOSITION_LABEL[d].toLowerCase()} {preview.totals[d]} {preview.totals[d] === 1 ? "record" : "records"}
              </span>
            ))}
          </div>
          <div className="mt-4 space-y-1.5" data-testid="privacy-preview-categories">
            {preview.categories.map((c) => (
              <div key={c.collection} className="border-b border-[#1A1B1A]/[0.04] pb-1.5">
                <div className="flex items-center justify-between gap-3 text-xs">
                  <span className="text-[#1A1B1A]">{c.label}</span>
                  <span className="flex items-center gap-2">
                    <span className="text-[#757675]">{c.records}</span>
                    <span className={`rounded-full px-2 py-0.5 text-[11px] ${DISPOSITION_STYLE[c.disposition]}`}>
                      {SUGGESTED_PREFIX}{DISPOSITION_LABEL[c.disposition].toLowerCase()}
                    </span>
                  </span>
                </div>
                <p className="text-[11px] text-[#757675] mt-0.5">{c.reason}</p>
              </div>
            ))}
          </div>
          {!preview.execution_enabled && (
            <p className="mt-4 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-xl p-3 flex gap-2"
              data-testid="privacy-execution-disabled">
              <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
              Permanent erasure is switched off in this environment. Requests are still recorded,
              reviewed and decided, and nothing is deleted.
            </p>
          )}
        </div>
      )}

      <div className="space-y-3" data-testid="privacy-request-list">
        {requests.map((r) => (
          <div key={r.id} className="bg-white rounded-2xl border border-[#1A1B1A]/[0.06] p-4"
            data-testid={`privacy-request-${r.id}`}>
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-xs font-medium text-[#1A1B1A]">
                  {r.request_type} &middot; {STATUS_LABEL[r.status] || r.status}
                </p>
                <p className="text-[11px] text-[#757675] mt-0.5">
                  Requested by {r.requester_name}{r.scope ? ` — ${r.scope}` : ""}
                </p>
                {r.decision_reason && (
                  <p className="text-[11px] text-[#757675] mt-0.5">Decision: {r.decision_reason}</p>
                )}
                {r.retention_basis && (
                  <p className="text-[11px] text-[#757675] mt-0.5" data-testid={`privacy-basis-${r.id}`}>
                    Legal basis for retained records: {r.retention_basis}
                  </p>
                )}
              </div>
              <div className="flex flex-wrap gap-1.5 justify-end">
                {r.status === "received" && (
                  <button onClick={() => act(r.id, "identity", { verified: true, detail: "Identity confirmed" }, "Identity verified")}
                    disabled={busy} data-testid={`privacy-verify-${r.id}`}
                    className="border border-[#1A1B1A]/15 text-[#4A4B4A] px-3 py-1 rounded-full text-[11px] hover:bg-[#FAF9F6]">
                    Verify identity
                  </button>
                )}
                {r.status === "identity_verified" && (
                  <button onClick={() => act(r.id, "review", {}, "Review started")}
                    disabled={busy} data-testid={`privacy-review-${r.id}`}
                    className="border border-[#1A1B1A]/15 text-[#4A4B4A] px-3 py-1 rounded-full text-[11px] hover:bg-[#FAF9F6]">
                    Start review
                  </button>
                )}
                {r.status === "under_review" && ["approved", "partially_approved", "refused"].map((d) => (
                  <button key={d} onClick={() => {
                    const reason = window.prompt(`Reason for '${d.replace("_", " ")}'`);
                    if (!reason) return;
                    const basis = window.prompt(
                      "Legal basis for anything being kept (optional)") || "";
                    act(r.id, "decision", { decision: d, reason, retention_basis: basis },
                      "Decision recorded");
                  }} disabled={busy} data-testid={`privacy-decide-${d}-${r.id}`}
                    className="border border-[#1A1B1A]/15 text-[#4A4B4A] px-3 py-1 rounded-full text-[11px] hover:bg-[#FAF9F6]">
                    {d.replace("_", " ")}
                  </button>
                ))}
                {["approved", "partially_approved", "refused"].includes(r.status) && (
                  <button onClick={() => act(r.id, "complete", { detail: "Request closed" }, "Request completed")}
                    disabled={busy} data-testid={`privacy-complete-${r.id}`}
                    className="bg-[#205B5A] text-white px-3 py-1 rounded-full text-[11px] hover:bg-[#174544]">
                    Mark completed
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
        {requests.length === 0 && (
          <p className="text-xs text-[#757675]" data-testid="privacy-no-requests">
            No privacy requests for this patient.
          </p>
        )}
      </div>
    </div>
  );
};

export default PrivacyDataRights;
